import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Zap,
  Cpu,
  Globe,
  FlaskConical,
  Briefcase,
  Image as ImageIcon,
  Palette,
  Trophy,
  Music,
  Film,
  Gamepad2,
  Heart,
  Utensils,
  Plane,
  BookOpen,
  Dumbbell,
  Newspaper,
  TrendingUp,
  Sparkles,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface Topic {
  id: string;
  name: string;
  slug: string;
  icon: string;
  sort_order: number;
}

interface OnboardingInterestsProps {
  onNext: (selectedTopicIds: string[]) => void | Promise<void>;
}

// Map topic slugs to Lucide icons
const TOPIC_ICON_MAP: Record<string, React.ReactNode> = {
  'breaking-now': <Zap size={20} />,
  news: <Newspaper size={20} />,
  tech: <Cpu size={20} />,
  world: <Globe size={20} />,
  science: <FlaskConical size={20} />,
  business: <Briefcase size={20} />,
  finance: <TrendingUp size={20} />,
  visual: <ImageIcon size={20} />,
  culture: <Palette size={20} />,
  sports: <Trophy size={20} />,
  music: <Music size={20} />,
  film: <Film size={20} />,
  entertainment: <Film size={20} />,
  gaming: <Gamepad2 size={20} />,
  lifestyle: <Heart size={20} />,
  food: <Utensils size={20} />,
  travel: <Plane size={20} />,
  education: <BookOpen size={20} />,
  fitness: <Dumbbell size={20} />,
  health: <Heart size={20} />,
};

// Topic colors for visual variety
const TOPIC_COLORS: Record<string, string> = {
  'breaking-now': 'border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300',
  news: 'border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300',
  tech: 'border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300',
  world: 'border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300',
  science: 'border-iris-500/30 bg-iris-500/10 hover:bg-iris-500/20 text-iris-300',
  business: 'border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300',
  finance: 'border-green-500/30 bg-green-500/10 hover:bg-green-500/20 text-green-300',
  visual: 'border-pink-500/30 bg-pink-500/10 hover:bg-pink-500/20 text-pink-300',
  culture: 'border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300',
  sports: 'border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 text-orange-300',
  music: 'border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 text-violet-300',
  film: 'border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300',
  entertainment: 'border-fuchsia-500/30 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-300',
  gaming: 'border-iris-500/30 bg-iris-500/10 hover:bg-iris-500/20 text-iris-300',
  lifestyle: 'border-teal-500/30 bg-teal-500/10 hover:bg-teal-500/20 text-teal-300',
  food: 'border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 text-orange-300',
  travel: 'border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300',
  education: 'border-lime-500/30 bg-lime-500/10 hover:bg-lime-500/20 text-lime-300',
  fitness: 'border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300',
  health: 'border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300',
};

const DEFAULT_COLOR = 'border-white/10 bg-white/4 hover:bg-white/8 text-[#a0a0b0]';
const SELECTED_COLOR = 'border-iris-500 bg-iris-500/20 text-white ring-2 ring-iris-500/50';

const MIN_SELECTIONS = 3;

export default function OnboardingInterests({ onNext }: OnboardingInterestsProps) {
  const { t } = useTranslation();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const getAuthToken = useCallback(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('authToken');
    return null;
  }, []);

  useEffect(() => {
    const fetchTopics = async () => {
      const token = getAuthToken();
      try {
        const res = await fetch(`${API_URL}/topics`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setTopics(data.topics || data || []);
        }
      } catch {
        // Use fallback topics if API fails
        setTopics([
          { id: '1', name: 'News', slug: 'news', icon: '', sort_order: 1 },
          { id: '2', name: 'Technology', slug: 'tech', icon: '', sort_order: 2 },
          { id: '3', name: 'Science', slug: 'science', icon: '', sort_order: 3 },
          { id: '4', name: 'Sports', slug: 'sports', icon: '', sort_order: 4 },
          { id: '5', name: 'Entertainment', slug: 'entertainment', icon: '', sort_order: 5 },
          { id: '6', name: 'Gaming', slug: 'gaming', icon: '', sort_order: 6 },
          { id: '7', name: 'Finance', slug: 'finance', icon: '', sort_order: 7 },
          { id: '8', name: 'Music', slug: 'music', icon: '', sort_order: 8 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, [getAuthToken]);

  const toggleTopic = (topicId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    if (selectedIds.size >= MIN_SELECTIONS) {
      setSaving(true);
      await onNext(Array.from(selectedIds));
      setSaving(false);
    }
  };

  const canContinue = selectedIds.size >= MIN_SELECTIONS;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-iris-500/15 flex items-center justify-center">
          <Sparkles size={28} className="text-iris-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('onboarding.interestsTitle')}
        </h3>
        <p className="text-sm text-[#9a9aac]">
          {t('onboarding.interestsSubtitle')}
        </p>
      </div>

      {/* Topics Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="text-iris-500 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {topics.map((topic) => {
            const isSelected = selectedIds.has(topic.id);
            const icon = TOPIC_ICON_MAP[topic.slug] || <Sparkles size={20} />;
            const colorClass = isSelected
              ? SELECTED_COLOR
              : TOPIC_COLORS[topic.slug] || DEFAULT_COLOR;

            return (
              <button
                key={topic.id}
                onClick={() => toggleTopic(topic.id)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all ${colorClass}`}
              >
                <span className="shrink-0">{icon}</span>
                <span className="text-sm font-medium truncate">{topic.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-white/6">
        <div className="text-sm text-[#9a9aac]">
          {selectedIds.size < MIN_SELECTIONS ? (
            <span>
              {t('onboarding.minimum', { min: MIN_SELECTIONS })}
            </span>
          ) : (
            <span className="text-emerald-400">
              {t('onboarding.selected', { count: selectedIds.size })}
            </span>
          )}
        </div>

        <button
          onClick={handleContinue}
          disabled={!canContinue || saving}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            canContinue && !saving
              ? 'bg-iris-600 hover:bg-iris-500 text-[#0C0C12]'
              : 'bg-white/4 text-[#828294] cursor-not-allowed'
          }`}
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          {saving ? t('onboarding.saving', 'Saving...') : t('onboarding.continue')}
        </button>
      </div>
    </div>
  );
}
